import { fieldLabelByKey } from './dataContext'
import type { EditTaskNodeData, WaitUnit } from 'app/types/workflows'

/** "1 day", "2 days", "30 minutes" — singularises the unit when duration is 1. */
export function formatDuration(duration: number, unit: WaitUnit): string {
  const singular = unit.replace(/s$/, ``)
  return `${duration} ${duration === 1 ? singular : unit}`
}

/** What an Edit task step does, in the order a person would say it: "Archive, if still open". */
export function editTaskSummary(data: EditTaskNodeData): string {
  const changes = [
    data.status === `closed` && `Close`,
    data.status === `open` && `Reopen`,
    data.archived === true && `Archive`,
    data.archived === false && `Unarchive`,
    data.ownerId !== undefined && (data.ownerId ? `Reassign` : `Unassign`),
    data.dueType && `Move the due date`,
    data.title?.trim() && `Rename`,
    data.description?.trim() && `Rewrite the description`,
  ].filter((change): change is string => !!change)
  if (!changes.length) return `Choose what to change`
  const text = changes.map((change, i) => (i === 0 ? change : change.toLowerCase())).join(`, `)
  return data.onlyIfOpen ? `${text}, if still open` : text
}

// —— The system's two clocks (TRK-5396, ruled by-design 2026-08-10) ——
// Call-family dates (Call.callDate, Call activities' activityDate, Call events' eventDate) store the
// LOCATION's wall clock in a UTC-shaped field. Everything else — engine dates (runs, workflows) and
// contact/appointment/task/message events — is real UTC. A wall-clock value must render literally
// (formatting in UTC shows the stored digits as-is, which IS the location-local reading); converting
// it like a real instant double-shifts the time on every screen. Key the choice off the record's type.

/** True when this event/activity type carries location wall-clock dates instead of real UTC. */
export function isWallClockType(type?: string): boolean {
  return type === `Call`
}

// These vendors' ingest runs submissionDate through convertUtz (trackable-api Form.ts), and a form's
// created event dates from it. ClickFunnels, Webflow and Trackable forms keep real UTC.
const WALL_CLOCK_FORM_VENDORS = new Set([`Jotform`, `Facebook`, `Gravity Form`])

export function isWallClockEvent(event: { type: string; action: string; data?: Record<string, unknown> | null }) {
  if (isWallClockType(event.type)) return true
  const vendor = (event.data?.vendor as { to?: unknown } | undefined)?.to
  return event.type === `Form` && event.action === `created` && WALL_CLOCK_FORM_VENDORS.has(String(vendor))
}

/** "Sep 16, 2026, 2:15 AM EDT". Real instants convert to `timeZone`, else the viewer's zone. Wall-clock
 *  digits already read on the location's clock, so they only borrow its name and stay unlabelled without one. */
export function formatEventTimestamp(iso: string | undefined, wallClock: boolean, timeZone?: string | null): string {
  if (!iso) return `·`
  const date = new Date(iso)
  const text = date.toLocaleString(`en-US`, {
    dateStyle: `medium`,
    timeStyle: `short`,
    timeZone: wallClock ? `UTC` : (timeZone ?? undefined),
  })
  if (wallClock && !timeZone) return text
  const zone = new Intl.DateTimeFormat(`en-US`, { timeZone: timeZone ?? undefined, timeZoneName: `short` })
    .formatToParts(date)
    .find((part) => part.type === `timeZoneName`)?.value
  return zone ? `${text} ${zone}` : text
}

/** Date-only variant for compact rows (trigger samples). */
export function formatEventDate(iso: string | undefined, type?: string): string {
  if (!iso) return ``
  return new Date(iso).toLocaleDateString(`en-US`, isWallClockType(type) ? { timeZone: `UTC` } : {})
}

/** A compiled trigger condition in words: "Procedure codes exactly D9243". */
export const filterPhrase = (filter: { attribute: string; operator: string; value?: string }) =>
  `${fieldLabelByKey(filter.attribute) ?? filter.attribute} ${filter.operator.replace(/_/g, ` `)}${filter.value ? ` ${filter.value}` : ``}`
